/**
 * Settings Page Skeleton
 * Matches the exact layout of the settings page
 *
 * Layout:
 * - Left: Settings Sidebar (w-80 lg:w-64 xl:w-80)
 *   - Header: "Settings" title (text-2xl) in container-padding py-6
 *   - Navigation: py-4 with space-y-1 px-3
 *     - 7 regular items (profile, business, phone, tags, templates, logging, archive)
 *     - Admin section (mt-8): "Admin Settings" header + 3 items
 *   - Nav item: icon (h-5 w-5 mr-3) + label
 *   - Version footer: px-6 py-4 with text-xs
 * - Right: Content area (flex-1)
 *   - Container: max-w-7xl mx-auto p-4 sm:p-6
 *   - Section header: icon (w-10 h-10 rounded-xl) + title (text-2xl) + subtitle
 *   - Card: bg-card-background rounded-lg shadow-sm border p-4 sm:p-6
 *     - Card header: icon (h-5 w-5) + title (text-lg)
 *     - Form fields
 *     - Save button with border-t
 */

import React from 'react';
import { Skeleton } from './Skeleton';

/**
 * Navigation item skeleton
 * Matches nav-item structure: icon + label
 */
const NavItemSkeleton: React.FC<{ width?: string }> = ({ width = 'w-28' }) => (
  <div className="flex items-center w-full px-3 py-2 rounded-lg">
    <Skeleton className="h-5 w-5 rounded mr-3 flex-shrink-0" />
    <Skeleton className={`h-4 ${width}`} />
  </div>
);

/**
 * Form field skeleton
 * Matches input field structure: label + input
 */
const FormFieldSkeleton: React.FC = () => (
  <div className="space-y-2">
    <div className="flex items-center mb-2">
      <Skeleton className="h-4 w-4 rounded mr-2" />
      <Skeleton className="h-4 w-24" />
    </div>
    <Skeleton className="h-10 w-full rounded-md" />
  </div>
);

/**
 * Toggle setting skeleton
 * Matches theme/text size toggle structure
 */
const ToggleSettingSkeleton: React.FC = () => (
  <div className="flex items-center justify-between">
    <div className="flex items-center space-x-3">
      <Skeleton className="w-8 h-8 rounded-lg" />
      <div>
        <Skeleton className="h-4 w-24 mb-1" />
        <Skeleton className="h-3 w-48" />
      </div>
    </div>
    <Skeleton className="h-6 w-11 rounded-full" />
  </div>
);

/**
 * Settings card skeleton
 * Matches card structure with header icon + title + content
 */
const SettingsCardSkeleton: React.FC<{
  titleWidth?: string;
  children: React.ReactNode;
}> = ({ titleWidth = 'w-40', children }) => (
  <div className="bg-card-background rounded-lg shadow-sm border border-border p-4 sm:p-6">
    <div className="flex items-center mb-4 sm:mb-6">
      <Skeleton className="h-5 w-5 rounded mr-2" />
      <Skeleton className={`h-5 ${titleWidth}`} />
    </div>
    {children}
  </div>
);

/**
 * Settings sidebar skeleton
 * Matches SettingsSidebar component layout
 */
const SidebarSkeleton: React.FC = () => (
  <div
    className="w-80 lg:w-64 xl:w-80 flex flex-col h-full bg-card-bg border-r border-border flex-shrink-0"
  >
    {/* Header - matches container-padding py-6 */}
    <div className="px-6 py-6">
      <Skeleton className="h-8 w-24" /> {/* "Settings" text-2xl */}
    </div>

    {/* Navigation - matches flex-1 overflow-y-auto py-4 */}
    <div className="flex-1 overflow-y-auto py-4">
      {/* Regular settings - 7 items */}
      <div className="space-y-1 px-3">
        <NavItemSkeleton width="w-32" /> {/* Profile & Display */}
        <NavItemSkeleton width="w-28" /> {/* Your Business */}
        <NavItemSkeleton width="w-28" /> {/* Phone Number */}
        <NavItemSkeleton width="w-28" /> {/* Tag Management */}
        <NavItemSkeleton width="w-36" /> {/* Message Templates */}
        <NavItemSkeleton width="w-32" /> {/* Message Logging */}
        <NavItemSkeleton width="w-36" /> {/* Archive & Download */}
      </div>

      {/* Admin section - matches mt-8 structure */}
      <div className="mt-8">
        {/* Admin Settings header - px-6 py-2, text-caption uppercase */}
        <div className="px-6 py-2">
          <Skeleton className="h-3 w-24" />
        </div>
        {/* Admin items - 3 items */}
        <div className="space-y-1 px-3">
          <NavItemSkeleton width="w-36" /> {/* Agency Management */}
          <NavItemSkeleton width="w-24" /> {/* Subscription */}
          <NavItemSkeleton width="w-40" /> {/* Systems & Integrations */}
        </div>
      </div>
    </div>

    {/* Version footer - px-6 py-4, text-xs */}
    <div className="px-6 py-4">
      <Skeleton className="h-3 w-32" />
    </div>
  </div>
);

/**
 * Settings content skeleton
 * Matches the content area layout structure
 */
const ContentSkeleton: React.FC = () => (
  <div className="flex-1 overflow-y-auto bg-background flex flex-col min-w-0">
    {/* Content container - max-w-7xl mx-auto p-4 sm:p-6 */}
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        {/* Section content - max-w-6xl mx-auto space-y-6 */}
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Section header - icon + title + subtitle */}
          <div className="flex items-center space-x-3">
            <Skeleton className="w-10 h-10 rounded-xl" />
            <div>
              <Skeleton className="h-7 w-56 mb-1" /> {/* text-2xl title */}
              <Skeleton className="h-4 w-72" /> {/* text-sm subtitle */}
            </div>
          </div>

          {/* Personal Information Card */}
          <SettingsCardSkeleton titleWidth="w-40">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Avatar section */}
              <div className="flex flex-col items-center space-y-4">
                <Skeleton className="w-24 h-24 sm:w-32 sm:h-32 rounded-full" />
                <div className="space-y-3 w-full flex flex-col items-center">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-5 w-36" />
                  <div className="flex gap-2 mt-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Skeleton key={i} className="w-6 h-6 sm:w-8 sm:h-8 rounded-full" />
                    ))}
                  </div>
                </div>
              </div>

              {/* Form fields */}
              <div className="space-y-4">
                <FormFieldSkeleton />
                <FormFieldSkeleton />
                <FormFieldSkeleton />
              </div>
            </div>

            {/* Save button */}
            <div className="flex justify-end pt-6 border-t border-border">
              <Skeleton className="h-10 w-32 rounded-lg" />
            </div>
          </SettingsCardSkeleton>

          {/* Display Settings Card */}
          <SettingsCardSkeleton titleWidth="w-36">
            <div className="space-y-8">
              <ToggleSettingSkeleton />
              <div className="space-y-4">
                <ToggleSettingSkeleton />
                <div className="space-y-4 pl-11">
                  <div className="flex items-center">
                    <Skeleton className="h-4 w-4 rounded-full mr-2" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <div className="flex items-center">
                    <Skeleton className="h-4 w-4 rounded-full mr-2" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                </div>
              </div>
            </div>

            {/* Save button */}
            <div className="flex justify-end pt-4 border-t border-border mt-6">
              <Skeleton className="h-10 w-40 rounded-lg" />
            </div>
          </SettingsCardSkeleton>
        </div>
      </div>
    </div>
  </div>
);

/**
 * Full settings page skeleton
 * Use this as the loading state for the settings page
 */
export const SettingsSkeleton: React.FC = () => (
  <div className="h-full w-full flex overflow-hidden rounded-2xl relative">
    <SidebarSkeleton />
    <ContentSkeleton />
  </div>
);

/**
 * Settings sidebar-only skeleton
 * Use this when only the sidebar needs a loading state
 */
export const SettingsSidebarSkeleton: React.FC = () => <SidebarSkeleton />;

/**
 * Settings content-only skeleton
 * Use this when only the content area needs a loading state
 */
export const SettingsContentSkeleton: React.FC = () => <ContentSkeleton />;

export default SettingsSkeleton;
